import { css } from "@emotion/react";
import type { Publication } from "@rowan/prisma";
import { useContext, useMemo, useState } from "react";
import { useParams } from "react-router";
import { trpc } from "../../utils/trpc";
import { BookmarkFilter } from "../bits/bookmarks/BookmarkFilter";
import { PublicationBookmarksRow } from "../bits/bookmarks/PublicationBookmarksRow";
import {
  FRJ,
  frjPublication,
  unknownPublicationTitle,
  unknownPublication,
} from "../bits/bookmarks/utils";
import { FlexColContainer } from "../bits/layout";
import { LoadingSpinner } from "../bits/loading";
import { SearchInput } from "../bits/search";
import { UserPublicationsContext } from "../contexts/WithPublications/UserPublicationsContext";
import { FilterHeader } from "../headers/FilterHeader";
import { EmptyCollectionPage } from "./EmptyCollectionPage";
import { NotFoundPage } from "./NotFoundPage";

export const BookmarksListPage = () => {
  const [search, setSearch] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");

  const { collectionId } = useParams();

  const { publications, isLoading: isPublicationsLoading } = useContext(
    UserPublicationsContext,
  );

  const { data: collectionData, isLoading: isCollectionLoading } =
    trpc.getCollections.useQuery();

  const collectionIdNumber = useMemo(() => {
    if (!collectionId) {
      return -1;
    }
    const parsed = Number(collectionId);
    return isNaN(parsed) ? -1 : parsed;
  }, [collectionId]);

  const collection = useMemo(() => {
    return collectionData?.find((c) => c.id === collectionIdNumber);
  }, [collectionData, collectionIdNumber]);

  const { data: bookmarksData, isLoading: isBookmarksLoading } =
    trpc.getBookmarksByCollection.useQuery(
      {
        collectionId: collectionIdNumber,
      },
      {
        enabled: collectionIdNumber > 0,
      },
    );

  const publicationsById = useMemo(() => {
    const byId = new Map<number, Publication>();
    (publications ?? []).forEach((publication) => {
      byId.set(publication.id, publication);
    });

    return byId;
  }, [publications]);

  // Group bookmarks by publication, filtered by search query
  const bookmarksByPublication = useMemo(() => {
    if (!bookmarksData || !publications) return [];

    const query = search.trim().toLowerCase();

    const filtered = query
      ? bookmarksData.filter((bookmark) => {
          if (bookmark.publicationId) {
            const publication = publicationsById.get(bookmark.publicationId);
            const publicationTitle = publication?.title?.toLowerCase() ?? "";
            const sectionTitle = bookmark.sectionTitle.toLowerCase();
            const parentSections = bookmark.parentSections
              .join(" ")
              .toLowerCase();

            return (
              sectionTitle.includes(query) ||
              parentSections.includes(query) ||
              publicationTitle.includes(query)
            );
          } else {
            if (bookmark.articleSlug) {
              const parentSections = bookmark.parentSections
                .join(" ")
                .toLowerCase();
              const sectionTitle = bookmark.sectionTitle.toLowerCase();

              return (
                sectionTitle.includes(query) || parentSections.includes(query)
              );
            }
            return false;
          }
        })
      : bookmarksData;

    const grouped: Record<string, typeof bookmarksData> = {};

    filtered.forEach((bookmark) => {
      const publicationId = bookmark.publicationId;
      const articleSlug = bookmark.articleSlug;
      if (!publicationId) {
        if (articleSlug) {
          if (!grouped[FRJ]) {
            grouped[FRJ] = [];
          }

          return grouped[FRJ].push(bookmark);
        } else {
          if (!grouped[unknownPublicationTitle]) {
            grouped[unknownPublicationTitle] = [];
          }

          return grouped[unknownPublicationTitle].push(bookmark);
        }
      }

      if (!grouped[publicationId]) {
        grouped[publicationId] = [];
      }

      grouped[publicationId].push(bookmark);
    });

    return Object.entries(grouped).flatMap(([publicationId, bookmarks]) => {
      const publication = publicationsById.get(Number(publicationId));

      if (publication) {
        return { publication, bookmarks };
      }

      if (publicationId === FRJ) {
        return [{ publication: frjPublication, bookmarks }];
      } else {
        return [{ publication: unknownPublication, bookmarks }];
      }
    });
  }, [bookmarksData, publications, publicationsById, search]);

  if (isCollectionLoading || isBookmarksLoading || isPublicationsLoading) {
    return <LoadingSpinner />;
  }

  if (!collection) {
    return <NotFoundPage message="Collection not found" />;
  }

  if (bookmarksData?.length === 0) {
    return <EmptyCollectionPage />;
  }

  return (
    <>
      <div
        css={css`
          border-left: 4px solid ${collection.colour};
        `}
      >
        <FilterHeader
          // Remove box-shadow from the left of the FilterHeader so the coloured line isn't obscured
          css={({ breakpoints }) => css`
            clip-path: inset(-10px -10px -10px 0px);

            @media (max-width: ${breakpoints.values.md}px) {
            flex-direction: column;
          `}
        >
          <h3
            css={({ breakpoints }) => css`
              color: ${collection.colour};
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;

              @media (max-width: ${breakpoints.values.md}px) {
                white-space: normal;
                word-break: break-word;
              }
            `}
          >
            {collection.name}
          </h3>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 16px;
              padding-right: 4px;
              width: auto;

              // between sm and md
              @media (min-width: 580px) {
                flex-direction: row;
                justify-content: flex-end;
              }
            `}
          >
            <SearchInput
              css={css`
                height: 32px;
              `}
              placeholder="Search bookmarks"
              aria-label="Search bookmarks"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <BookmarkFilter
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
            />
          </div>
        </FilterHeader>
        <FlexColContainer>
          {bookmarksByPublication.length === 0 && search.trim() ? (
            <div
              css={css`
                padding: 16px;
              `}
            >
              No bookmarks matching &ldquo;{search.trim()}&rdquo;
            </div>
          ) : (
            bookmarksByPublication.map(({ publication, bookmarks }) => {
              if (
                selectedFilter === "publications" &&
                publication.title === FRJ
              ) {
                return null;
              }

              if (
                selectedFilter === "frj_articles" &&
                publication.title !== FRJ
              ) {
                return null;
              }
              return (
                <PublicationBookmarksRow
                  key={publication.id}
                  publication={publication}
                  bookmarks={bookmarks}
                  collection={collection}
                />
              );
            })
          )}
        </FlexColContainer>
      </div>
    </>
  );
};
